import React, {useState, useEffect} from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Container, Typography, FormControl, Input, InputAdornment, Skeleton, Button, Paper, ButtonGroup } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useViewPropertyQuery, useGetAllUserAccessQuery } from '../services/dvagent'
import { Property } from '../sections/@dashboard/manageProperty';
import { Placeholder, Toaster } from '../sections/@dashboard/attributes';
import { Unauthorized } from '../sections/@dashboard/manageUser';
import { PropertyCard } from '../components/skeleton';
import { getToken, getUsername } from '../services/LocalStorageService';
import Iconify from '../components/iconify';
import serverUrl from '../_mock/account'

export default function Properties() {

  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('grid');
  const [haveCreateAccess, setHaveCreateAccess] = useState(false)
  const { data:propertyData, isSuccess:propertyIsSuccess, error:PropertyError } = useViewPropertyQuery({"token":getToken(), "location":location});
  const { data:userAccessData, isSuccess:userAccessIsSuccess, error:userAccessError } = useGetAllUserAccessQuery({"token":getToken(), "location":location});
  const paginationModel = { page: 0, pageSize: 10 };
  const baseUrl = serverUrl.serverUrl; 
  const [toasterData, setToasterData] = useState({
    trigger: false,
    status: true,
    symbol: '✔',
    message: '',
  });

  const columns = [
    { field: 'serialNumber', headerName: 'S. No.',},
    {
      field: 'name',
      headerName: 'Name',
      flex: 1, 
      renderCell: (params) => (
        <RouterLink 
          to="/cloud/update-property"
          state={params.row.id}
          key={params.row.id}
          style={{ textDecoration: 'none', color: '#000', fontWeight: 'bold' }}
        >
        {params.row.name}
        </RouterLink>
        )
    },
    { field: 'location', headerName: 'Location', flex: 1 },
  ];

  useEffect(() => {
    setLoading(false);
    if ((PropertyError && PropertyError.status === 401) || 
        (userAccessError && userAccessError.status === 401)) {
      // showToast(false, 'ⓘ', 'Unauthorized User');
    } else if (PropertyError || userAccessError) {
      showToast(false, 'ⓘ', 'Something went wrong, server error');
    }
    if (userAccessData && userAccessIsSuccess){
      try{
        setHaveCreateAccess(userAccessData.data.have_create_access)
      } catch {
        setHaveCreateAccess(false)
      }
    }
    if (propertyData && propertyIsSuccess) {
      setProperties(propertyData.properties)
    }
    setTimeout(() => {
      setLoading(true);
    }, 1000);
  }, [propertyData, propertyIsSuccess, PropertyError, userAccessData, userAccessIsSuccess, userAccessError]);  


  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredProperty = properties.filter(
    (item) =>
      item.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const rows = filteredProperty.map((row, index) => ({
    ...row,
    serialNumber: index + 1,
  }));

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const showToast = (status, symbol, message) => {
    setToasterData({
      trigger: true,
      status,
      symbol,
      message,
    });

    setTimeout(() => {
      setToasterData((prev) => ({
        ...prev,
        trigger: false,
      }));
    }, 5000); 
  };
  return (
    <>
      <Helmet>
        <title> DV Agent </title>
      </Helmet>
      {toasterData.trigger && (
        <Toaster
          trigger={toasterData.trigger}
          status={toasterData.status}
          symbol={toasterData.symbol}
          message={toasterData.message}
        />
      )}
      <Container maxWidth="xxl">
        {loading ? (
          <>
            <Grid container spacing={3} sx={{mb : 2}} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  Hi {loading ? getUsername() : <></>}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={6}>
                <FormControl
                  variant="standard"
                  fullWidth
                >
                  <Input
                    id="standard-adornment-amount"
                    variant="standard"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search"
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>

            <ButtonGroup
              variant="outlined"
              aria-label="Disabled button group"
              sx={{ mb: 2 }}
            >
              <Button
                onClick={() => handleViewChange('grid')}
                variant={view === 'grid' ? 'contained' : 'outlined'}
              >
                <Iconify icon="line-md:grid-3-filled" />
                &nbsp;Grid
              </Button>
              <Button
                onClick={() => handleViewChange('list')}
                variant={view === 'list' ? 'contained' : 'outlined'}
              >
                <Iconify icon="line-md:list-3-filled" />
                &nbsp;List
              </Button> 
            </ButtonGroup>
            { properties.length === 0 && !haveCreateAccess? (
              <Unauthorized/>
            ): view === 'grid' ? (
                <Grid container spacing={3}>
                  {haveCreateAccess && 
                    <Grid item xs={12} sm={6} md={4} xl={3} to="/cloud/add-property" component={RouterLink}>
                      <Placeholder/>
                    </Grid>
                  }
                  { filteredProperty.map((item) =>
                      <Grid item xs={12} sm={6} md={4} xl={3} to="/cloud/update-property" state={item.id} component={RouterLink} key={item.id} style={{ textDecoration: 'none' }}>
                        <Property thumbnail={baseUrl + item.thumbnail} name={item.name} location={item.location} />
                      </Grid>
                  )}
                </Grid>
              ):(
                <Paper>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    initialState={{ pagination: { paginationModel } }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    checkboxSelection
                    sx={{
                      border: 0,
                      width: '100%',
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#afb3c7', 
                        color: '#A8A196',          
                        fontSize: '16px',          
                        fontWeight: 'bold',        
                      },
                      '& .MuiDataGrid-columnSeparator': {
                        display: 'none',           
                      },
                    }}
                  />
                </Paper>
              )
            }
          </>
        ):(
          <>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  <Skeleton animation="wave" height={40} width="30%" />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={6}>
                <FormControl
                  variant="standard"
                  fullWidth
                >
                  <Input
                    id="standard-adornment-amount"
                    variant="standard"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search"
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  <Skeleton animation="wave" height={60} width="25%" />
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} xl={3} key={index}>
                  <PropertyCard/>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </>
  );
}

